import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import {
  useFlagPhoto,
  useI18N,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AddPhotoImageData } from "common/types";
import Zoom from "react-medium-image-zoom";
import { createPageAnalytics } from "common/analytics";
import { PhotoGuardrailError } from "common/apis";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useGuardrailImageFeedback } from "@/hooks/useGuardrailImageFeedback";
import WideButton from "@/components/common/WideButton";
import { Storage } from "common/storage";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

type FlagVenuePhotoProps = {
  photoReviewId: string;
  venueName: string;
  isOpen: boolean;
  onClose: () => void;
  venueDetails: any;
};

function FlagVenuePhoto({
  photoReviewId,
  venueName,
  isOpen,
  onClose,
  venueDetails,
}: FlagVenuePhotoProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();

  const showToast = useCustomToast();

  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const isZoomedIn = useZoomDetection();

  const { mutate, error, isPending, data } = useFlagPhoto();

  // DEV-444 — classify + surface the photo content-safety guardrail outcome.
  const guardrail = useGuardrailImageFeedback(data, error, {
    savedLabel: i18n.get("photoGuardrailReportSaved"),
  });

  // flag_photo_form page. photo_index isn't known inside the form (the opener has it) -> 0;
  // place_header_name/place_space_name aren't passed -> "".
  const flagFormA = createPageAnalytics("flag_photo_form");
  const flagPhotoProps = () => {
    const venue = venueDetails?.data ?? venueDetails;
    return {
      brand_id: venue?.brandId != null ? String(venue.brandId) : "",
      brand_name: venue?.brandName ?? "",
      business_id: String(venue?.id ?? ""),
      photo_index: 0,
      place_header_name: "",
      place_name: venueName ?? "",
      place_space_name: "",
    };
  };
  const flagOpenedFiredRef = useRef(false);
  useEffect(() => {
    if (isOpen && !flagOpenedFiredRef.current) {
      flagOpenedFiredRef.current = true;
      flagFormA.track("page_view_page", flagPhotoProps());
    } else if (!isOpen) {
      flagOpenedFiredRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Guardrail failures (hard rejection / outage) are surfaced by
  // useGuardrailImageFeedback; here we only (a) show the generic toast for
  // genuine non-guardrail errors and (b) prune the rejected photos so the user
  // can retry with the modal still open.
  useEffect(() => {
    if (!error) return;
    if (error instanceof PhotoGuardrailError) {
      if (guardrail.rejectedInputIndexes.length) {
        setAttachedImages((prev) =>
          prev.filter((_, i) => !guardrail.rejectedInputIndexes.includes(i)),
        );
      }
      return;
    }
    showToast(error.message, "error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isDark]);

  useEffect(() => {
    if (!data) return;
    // Full success -> plain success toast. Partial success -> the single combined
    // "Report saved — N photo(s) wasn't attached" toast from the guardrail hook.
    if (!guardrail.isPartial) {
      showToast(data.message, "success");
    }
    setAttachedImages([]);
    setNote("");
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDark]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (note.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    setErrorMessage(null);

    // submit_flag_action_button -> photo_flagged.
    flagFormA.track("submit_flag_action_button", {
      ...flagPhotoProps(),
      photos_count: venueDetails?.data?.business_photos?.length ?? 0,
    });

    mutate({
      photoReviewId,
      accessToken: Storage.getCurrentUserToken(),
      notes: note,
      images: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {isPending && <Loader />}
      <div
        style={modalStyle}
        className={cn(modalClassName)}
        aria-labelledby="modal-title"
      >
        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
        >
          <StandardButton
            onClick={() => {
              // close_action_button -> flag_photo_form_closed.
              flagFormA.track("close_action_button", flagPhotoProps());
              setAttachedImages([]);
              setNote("");
              onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <h1
            className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4"
            ref={firstFocusRef}
            id="modal-title"
          >
            {i18n.get("flagPhoto")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>{" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                {venueName}
              </div>
              <h2 className="self-stretch text-fg-neutral-strong title-s-semibold">
                {i18n.get("helpUsCatchEveryError")}
              </h2>
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-1">
                {i18n.get("haveYouSpottedSomethingWrong")}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="w-full">
                <label htmlFor="review" className="sr-only">
                  {i18n.get("tellUsWhyThisPhotoIsIncorrect")}
                </label>
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsWhyThisPhotoIsIncorrect")}
                  className="scrollbar-thin h-28"
                  value={note}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                  error={(note.length > 0 && note.length < 5) || Boolean(errorMessage)}
                />
                <div
                  id="charCount"
                  className={`label-s-medium mt-3 ${
                    note.length > 0 && note.length < 5
                      ? "text-fg-error-subtle"
                      : "text-fg-neutral-subtle"
                  }`}
                >
                  {note.length > 0 && note.length < 5
                    ? i18n.get("pleaseEnterAtLeast5Characters")
                    : `${note.length}/500 characters`}
                </div>
                {errorMessage && !(note.length > 0 && note.length < 5) && (
                  <div className="text-fg-error-subtle" role="alert">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt={`Attached image ${index + 1}`}
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default z-1000 ${!isZoomedIn ? "sticky bottom-0" : "relative"}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture"
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
              >
                <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                  aria-label="Upload photos"
                />
              </label>
            </div>

            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit flag"
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>
      </div>
    </Modal>
  );
}

export default FlagVenuePhoto;
